import type { TFunction } from 'i18next';

import type { LibraryKind, MediaLocation, MediaSourceOption, MediaSourceProvider } from '../../api/types';
import { pluginKey } from '../../i18n';

/// A source is named by its own translated label. "tv.emiro.filesystem" is an identifier for the
/// API to carry, not something to show a person.
///
/// The application's own key first, then the plugin's: a plugin ships its catalogue namespaced
/// by its id, so "provider.xtream.name" is served as "plugin.tv.emiro.xtream.provider.xtream.name"
/// and asking for the bare key finds nothing. Where neither has it, the id is the honest
/// fallback rather than a key shown to a person.
export function sourceName(t: TFunction, provider: Pick<MediaSourceProvider, 'providerId' | 'displayNameKey'>) {
  return t([provider.displayNameKey, pluginKey(provider.providerId, provider.displayNameKey)], {
    defaultValue: provider.providerId,
  });
}

/// "Provider X · Movies": whose it is and what it holds. A source with only one thing to offer
/// is named by the source itself.
export function optionName(t: TFunction, provider: MediaSourceProvider, option: MediaSourceOption) {
  return `${option.label ?? sourceName(t, provider)} · ${t(`libraryKind.${option.content}`)}`;
}

/// Whether a library of this kind takes the option. A mixed library takes anything; any other
/// only what holds its own kind, because the scanner files everything in a library as its kind.
export function fits(option: MediaSourceOption, kind: LibraryKind) {
  return kind === 'Mixed' || option.content === kind;
}

/// The kind a library made of these options has: theirs where they agree, mixed where they do
/// not. Nobody has to be asked what a library of one account's films is.
export function kindOf(options: MediaSourceOption[]): LibraryKind | undefined {
  const kinds = new Set(options.map((option) => option.content));

  if (kinds.size === 0) {
    return undefined;
  }

  return kinds.size === 1 ? [...kinds][0] : 'Mixed';
}

/// The locations a library reads from after an edit. An address that was already there keeps
/// its location id - that id is what everything catalogued under it points at, and a new one
/// would make the scanner read every title in it again as new.
///
/// For what is saved, not for what is being typed: addresses are trimmed, left out where empty,
/// and each is taken once - the same folder typed twice is one location, not two rows claiming the
/// same id.
export function locationsFor(providerId: string, addresses: string[], existing: MediaLocation[] = []): MediaLocation[] {
  const distinct = [...new Set(addresses.map((address) => address.trim()).filter((address) => address.length > 0))];

  return distinct.map(
    (address) =>
      existing.find((location) => location.providerId === providerId && location.address === address) ?? {
        id: crypto.randomUUID(),
        providerId,
        address,
      },
  );
}
